import type { MetricValueFilter } from "../types"

const ENTRY_SEP = ","
const FIELD_SEP = "|"

// ── Encoding helpers ─────────────────────────────────────────────────

function encodePart(s: string): string {
  // Encode our delimiters and percent so round-tripping is lossless
  return s.replace(/%/g, "%25").replace(/\|/g, "%7C").replace(/,/g, "%2C")
}

function decodePart(s: string): string {
  return s.replace(/%2C/gi, ",").replace(/%7C/gi, "|").replace(/%25/g, "%")
}

// ── MetricValueFilter[] ──────────────────────────────────────────────
// Format: metricId|displayName|operator|value|valueType
// Multiple entries separated by commas

export function serializeMetricValueFilters(
  filters: MetricValueFilter[],
): string {
  if (!filters.length) {
    return ""
  }
  return filters
    .map((f) =>
      [
        encodePart(f.metricId),
        encodePart(f.metricDisplayName),
        encodePart(f.operator),
        encodePart(f.value),
        f.valueType,
      ].join(FIELD_SEP),
    )
    .join(ENTRY_SEP)
}

export function parseMetricValueFilters(raw: string): MetricValueFilter[] {
  if (!raw) {
    return []
  }
  const filters: MetricValueFilter[] = []
  for (const entry of raw.split(ENTRY_SEP)) {
    const parts = entry.split(FIELD_SEP)
    if (parts.length !== 5) {
      continue
    }
    const [metricId, metricDisplayName, operator, value, valueType] = parts
    if (valueType !== "string" && valueType !== "float") {
      continue
    }
    const validOps = [">", "<", "=", ">=", "<=", "exists"]
    const decodedOp = decodePart(operator)
    if (!validOps.includes(decodedOp)) {
      continue
    }
    filters.push({
      metricId: decodePart(metricId),
      metricDisplayName: decodePart(metricDisplayName),
      operator: decodedOp as MetricValueFilter["operator"],
      value: decodePart(value),
      valueType,
    })
  }
  return filters
}
